// System
import { useCallback, useRef, useState } from 'react';
import { Box, Avatar, Button, Popover, Stack } from '@mui/material';

// Components
import { AssetIcon } from '@Components/shared/images/AssetIcon';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';

// Hooks
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

const AccountDropDown = () => {
  const { handleAccountLogout, currentUser } = useAuthorization();

  const [open, setOpen] = useState(false);
  const [showAllProviders, setShowAllProviders] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setShowAllProviders(false); // Reset show all when closing dropdown
  }, []);

  const handleToggleProviders = useCallback(() => {
    setShowAllProviders((prev) => !prev);
  }, []);

  const renderProviderIds = () => {
    if (!currentUser.providerIds || currentUser.providerIds.length === 0) {
      return null;
    }

    if (currentUser.providerIds.length === 1) {
      return (
        <Stack display="flex" flexDirection="column" gap="4px" alignItems="flex-end">
          <TruncateText text={currentUser.providerIds[0]} maxLength={8} />
        </Stack>
      );
    }

    if (showAllProviders) {
      return (
        <Stack display="flex" flexDirection="column" gap="4px" alignItems="flex-end">
          {currentUser.providerIds.map((providerId, index) => (
            <TruncateText key={index} text={providerId} maxLength={8} />
          ))}
          <Button
            variant="text"
            size="small"
            onClick={handleToggleProviders}
            sx={{
              fontSize: '12px',
              color: 'rgb(99, 115, 129)',
              textTransform: 'none',
              padding: '2px 4px',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Show less
          </Button>
        </Stack>
      );
    }

    return (
      <Stack display="flex" flexDirection="column" gap="4px" alignItems="flex-end">
        <TruncateText text={currentUser.providerIds[0]} maxLength={8} />
        <Button
          variant="text"
          size="small"
          onClick={handleToggleProviders}
          sx={{
            fontSize: '12px',
            color: 'rgb(99, 115, 129)',
            textTransform: 'none',
            padding: '2px 4px',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Show more ({currentUser.providerIds.length - 1})
        </Button>
      </Stack>
    );
  };

  return (
    <>
      {currentUser && (
        <Box sx={{ position: 'absolute', right: '50px', top: '20px' }}>
          <Box ref={anchorRef}>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<Avatar sx={{ height: 20, width: 20 }} />}
              endIcon={<AssetIcon src="chevron-down" size="s" />}
              onClick={handleClick}
            ></Button>
          </Box>
          <Popover
            open={open}
            anchorEl={anchorRef.current}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ marginTop: 1 }}
          >
            <Box
              sx={{
                width: '329px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <Stack
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack sx={{ fontSize: '14px', lineHeight: '24px', color: 'rgb(99, 115, 129)' }}>
                  User ID
                </Stack>
                <Stack sx={{ fontSize: '14px', lineHeight: '24px', color: 'rgb(0, 0, 0)' }}>
                  {currentUser.id ? <TruncateText text={currentUser.id} maxLength={8} /> : null}
                </Stack>
              </Stack>
              <Stack
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack sx={{ fontSize: '14px', lineHeight: '24px', color: 'rgb(99, 115, 129)' }}>
                  Email
                </Stack>
                <Stack sx={{ fontSize: '14px', lineHeight: '24px', color: 'rgb(0, 0, 0)' }}>
                  {currentUser.email ? currentUser.email : null}
                </Stack>
              </Stack>
              <Stack
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack sx={{ fontSize: '14px', lineHeight: '24px', color: 'rgb(99, 115, 129)' }}>
                  Provider IDS
                </Stack>
                <Stack sx={{ fontSize: '14px', lineHeight: '24px', color: 'rgb(0, 0, 0)' }}>
                  {renderProviderIds()}
                </Stack>
              </Stack>
              <Button
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '8px',
                  backgroundColor: 'rgb(223, 227, 232)',
                }}
                onClick={() => handleAccountLogout()}
              >
                <Box color="rgb(33, 43, 54)">Log out</Box>
              </Button>
            </Box>
          </Popover>
        </Box>
      )}
    </>
  );
};

export default AccountDropDown;
